import { useCallback, useEffect, useState } from 'react';
import { Title } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { useHasPermission } from '../PermissionGate';
import {
  FEATURE_TRANSPOREON,
  MISSING_IN_TMS_RESOURCE,
  fetchMissingInTms,
  freshnessNote,
  type MissingInTmsList,
} from './transporeonApi';

/**
 * „Transporeon — brak w TMS” dla FTL (decyzja Bartka 04.11.2026, przełącznik
 * `transporeonImport`). Transporty przyjęte w Transporeonie, których po
 * godzinie nie ma w TMS — ta sama reguła co kafelek pulpitów FTL i znacznik
 * na ekranie administracyjnym. Tylko do odczytu, bez surowych komunikatów
 * i danych dyspozytorów: zleceniodawca, numer, relacja, okno załadunku, od
 * kiedy brak. Czas polski.
 */

export const MISSING_FEATURE_OFF_NOTE =
  'Lista „Transporeon — brak w TMS” jest wyłączona w wersji aplikacji, z której korzystasz.';
export const MISSING_NO_PERMISSION_NOTE = 'Lista „Transporeon — brak w TMS” jest dla planowania FTL.';
export const MISSING_INTRO =
  'Transporty, które zleceniodawca przydzielił w Transporeonie, a których po godzinie nadal nie ma w TMS pod tym numerem. Sprawdź z działem przyjęcia zleceń, czy zlecenie trafi do TMS. Lista tylko do odczytu — znika z niej transport, który pojawił się w TMS, został anulowany albo którego okno minęło.';
export const MISSING_EMPTY = 'Brak transportów z Transporeonu, których nie ma w TMS.';

const PER_PAGE = 50;

/** „04.11.2026, 18:00 – 22:00” albo dwie pełne chwile, gdy okno przechodzi przez północ. */
export function windowText(from: string | null, until: string | null): string {
  if (!from && !until) return '—';
  if (!from || !until) return dateTimeNoSeconds(from ?? until);
  const a = dateTimeNoSeconds(from);
  const b = dateTimeNoSeconds(until);
  const [dayA] = a.split(', ');
  const [dayB, timeB] = b.split(', ');
  return dayA === dayB ? `${a} – ${timeB}` : `${a} – ${b}`;
}

export const TransporeonMissingScreen = () => {
  const on = useFeatureState(FEATURE_TRANSPOREON);
  const allowed = useHasPermission('FTL_PLANNING');
  if (on === undefined) return <FeaturesLoading />;
  if (!on || !allowed) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel(MISSING_IN_TMS_RESOURCE)} />
        <Alert severity="info">{on ? MISSING_NO_PERMISSION_NOTE : MISSING_FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <TransporeonMissingBody />;
};

const TransporeonMissingBody = () => {
  const [page, setPage] = useState(0);
  const [data, setData] = useState<MissingInTmsList | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchMissingInTms(page, PER_PAGE));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const note = freshnessNote(data?.freshness);
  const pages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;

  return (
    <Stack spacing={2} sx={{ p: 2 }} data-testid="transporeon-missing-screen">
      <Title title={menuLabel(MISSING_IN_TMS_RESOURCE)} />
      <Typography variant="body2" color="text.secondary">
        {MISSING_INTRO}
      </Typography>
      {note ? (
        <Alert severity="warning" data-testid="transporeon-missing-freshness">
          {note}. Liczba to stan z naszej bazy.
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!data && !error ? <CircularProgress size={24} /> : null}
      {data ? (
        <>
          <Typography variant="subtitle2" data-testid="transporeon-missing-total">
            Transporty bez zlecenia w TMS: {data.total}
          </Typography>
          {data.items.length === 0 ? (
            <Alert severity="success">{MISSING_EMPTY}</Alert>
          ) : (
            <Box sx={TABLE_SCROLL_SX}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Zleceniodawca</TableCell>
                    <TableCell>Numer z Transporeonu</TableCell>
                    <TableCell>Relacja</TableCell>
                    <TableCell>Okno załadunku</TableCell>
                    <TableCell>Brak w TMS od</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((t) => (
                    <TableRow key={t.id} data-testid="transporeon-missing-row">
                      <TableCell>{t.shipperName ?? '—'}</TableCell>
                      <TableCell>{t.number}</TableCell>
                      <TableCell>{t.route ?? '—'}</TableCell>
                      <TableCell>{windowText(t.loadingFrom, t.loadingUntil)}</TableCell>
                      <TableCell>{dateTimeNoSeconds(t.missingSince)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {pages > 1 ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Poprzednia
              </Button>
              <Typography variant="caption">
                Strona {page + 1} z {pages}
              </Typography>
              <Button size="small" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
                Następna
              </Button>
            </Stack>
          ) : null}
          <Box>
            <Button size="small" onClick={() => void load()}>
              Odśwież
            </Button>
          </Box>
        </>
      ) : null}
    </Stack>
  );
};
